import React, { useState, useContext } from 'react';
import { IcheerContext } from '../Context/AppContext'; // Adjust path as needed
import { Mail, Lock, Send, Activity, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

import Logo from '../components/Logo';

const PasswordResetPage = () => {
  const { host, setIsAdminLoggedIn } = useContext(IcheerContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1: Request OTP, 2: Enter OTP and new password
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmNewPassword: '',
    frontendOtp: ''
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const requestPasswordOTP = async (email) => {
    const response = await fetch(`${host}team/request-password-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  };

  const updatePassword = async (passwordData) => {
    
    const response = await fetch(`${host}team/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password update failed');
    }

    return response.json();
  };

  const handleRequestOTP = async () => {
    if (!formData.email) {
      setMessage('Please enter your email');
      setMessageType('danger');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage('Please enter a valid email address');
      setMessageType('danger');
       return;
    }

    setLoading(true);
    try {
      const response = await requestPasswordOTP(formData.email);
      setMessage('OTP sent to your email successfully!');
      setMessageType('success');
      setOtp(response.otp); // In production, don't show OTP in response
      setStep(2);
    } catch (error) {
      setMessage(error.message || 'Failed to send OTP');
      setMessageType('danger');
    }
    setLoading(false);
  };

  const handlePasswordUpdate = async () => {
    if (!formData.newPassword || !formData.confirmNewPassword || !formData.frontendOtp) {
      setMessage('Please fill in all fields');
      setMessageType('danger');
      return;
    }

    if (formData.newPassword !== formData.confirmNewPassword) {
      setMessage('Passwords do not match');
      setMessageType('danger');
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long');
      setMessageType('danger');
      return;
    }

    if (formData.frontendOtp.length < 6) {
      setMessage('OTP must be at least 6 characters long');
      setMessageType('danger');
      return;
    }

    if (formData.frontendOtp !== otp) {
      setMessage('Wrong OTP');
      setMessageType('danger');
      return;
    }

    setLoading(true);
    try {
      const passwordData = {
        email: formData.email,       // make sure this is not empty
        new_password: formData.newPassword,
        otp: formData.frontendOtp    // optional now if you removed OTP check
      };

      await updatePassword(passwordData);
      setMessage('Password updated successfully! You can now log in with your new password.');
      setMessageType('success');
      localStorage.setItem('isAdminLoggedIn', 'true');
      localStorage.setItem('userEmail', formData.email);
      setIsAdminLoggedIn(true);
      navigate('/') // Redirect to login after 2 seconds
    } catch (error) {
      setMessage(error.message || 'Password update failed');
      setMessageType('danger');
    }
    setLoading(false);
  };

  const handleKeyPressStep1 = (e) => {
    if (e.key === 'Enter') {
      handleRequestOTP();
    }
  };

  const handleKeyPressStep2 = (e) => {
    if (e.key === 'Enter') {
      handlePasswordUpdate();
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center" style={{
      background: '#dc59a0'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-5">
            {/* Header */}
            <div className="text-center mb-4">
              <Logo />
            </div>
            {/* Password Reset Card */}
            <div className="card shadow-lg border-0" style={{ borderRadius: '15px' }}>
              <div className="card-header bg-white border-0 text-center py-4" style={{ borderRadius: '15px 15px 0 0' }}>
                <h4 className="mb-0 text-dark fw-bold">Reset Your Password</h4>
                <p className="text-muted mb-0 mt-1">
                  {step === 1 ? 'Step 1 of 2 - Email Verification' : 'Step 2 of 2 - New Password'}
                </p>
              </div>

              <div className="card-body p-4">
                {step === 1 ? (
                  <div>
                    <div className="text-center mb-4">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '60px', height: '60px' }}>
                        <Mail size={24} className="text-primary" />
                      </div>
                      <p className="text-muted">Enter your email address to receive a verification code</p>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="email" className="form-label text-dark fw-semibold">
                        <Mail size={16} className="me-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep1}
                        placeholder="Enter your email address"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}
                      />
                    </div>

                    {message && (
                      <div className={`alert alert-${messageType} alert-dismissible border-0 shadow-sm mb-4`} role="alert">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">{message}</div>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setMessage('')}
                          ></button>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-primary btn-lg w-100 fw-semibold shadow-sm mb-3"
                      onClick={handleRequestOTP}
                      disabled={loading}
                      style={{
                        borderRadius: '10px',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none'
                      }}
                    >
                      {loading ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Sending verification code...
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          <Send size={18} className="me-2" />
                          Send Verification Code
                        </div>
                      )}
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="text-center mb-4">
                      <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '60px', height: '60px' }}>
                        <Lock size={24} className="text-success" />
                      </div>
                      <p className="text-muted">Enter the OTP and your new password</p>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="email" className="form-label text-dark fw-semibold">
                        <Mail size={16} className="me-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control border-0 shadow-sm"
                        value={formData.email}
                        disabled
                        style={{
                          backgroundColor: '#e9ecef',
                          borderRadius: '8px'
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="frontendOtp" className="form-label text-dark fw-semibold">
                        <Activity size={16} className="me-2" />
                        OTP (One-Time Password)
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg border-0 shadow-sm"
                        id="frontendOtp"
                        name="frontendOtp"
                        value={formData.frontendOtp}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPressStep2}
                        placeholder="Enter your OTP"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '10px',
                          padding: '12px 16px'
                        }}
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label text-dark fw-semibold">
                        <Lock size={16} className="me-2" />
                        New Password
                      </label>
                      <div className="position-relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="newPassword"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="Enter your new password (min 6 characters)"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 45px 12px 16px'
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          style={{ border: 'none', background: 'none' }}
                        >
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="confirmNewPassword" className="form-label text-dark fw-semibold">
                        <Lock size={16} className="me-2" />
                        Confirm New Password
                      </label>
                      <div className="position-relative">
                        <input
                          type={showConfirmNewPassword ? 'text' : 'password'}
                          className="form-control form-control-lg border-0 shadow-sm"
                          id="confirmNewPassword"
                          name="confirmNewPassword"
                          value={formData.confirmNewPassword}
                          onChange={handleInputChange}
                          onKeyPress={handleKeyPressStep2}
                          placeholder="Confirm your new password"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '10px',
                            padding: '12px 45px 12px 16px'
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2"
                          onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                          style={{ border: 'none', background: 'none' }}
                        >
                          {showConfirmNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    {message && (
                      <div className={`alert alert-${messageType} alert-dismissible border-0 shadow-sm mb-4`} role="alert">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">{message}</div>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setMessage('')}
                          ></button>
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-success btn-lg w-100 fw-semibold shadow-sm mb-3"
                      onClick={handlePasswordUpdate}
                      disabled={loading}
                      style={{
                        borderRadius: '10px',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        border: 'none'
                      }}
                    >
                      {loading ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Updating password...
                        </div>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center">
                          <Lock size={18} className="me-2" />
                          Update Password
                        </div>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100"
                      onClick={() => setStep(1)}
                      disabled={loading}
                      style={{ borderRadius: '8px' }}
                    >
                      <ArrowLeft size={16} className="me-2" />
                      Back to Email Step
                    </button>
                  </div>
                )}

                <div className="text-center mt-4">
                  <p className="text-muted mb-0">
                    Remember your password?{' '}
                    <a href="/login" className="text-decoration-none fw-semibold" style={{ color: '#667eea' }}>
                      Sign in here
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;